import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { PageShell } from '../components/stage/PageShell.js';
import { GlyphCheckCircle, GlyphMic, GlyphPhone } from '../components/ui/Glyphs.js';
import { Skeleton } from '../components/ui/Skeleton.js';
import { characterName, DEFAULT_CHARACTER } from '../lib/characters.js';
import { assistantHeld, liveServiceAvailable, openAssistantSettings } from '../lib/liveService.js';
import { useProfile } from '../lib/useProfile.js';
import '../components/conta/conta.css';

/**
 * Assistente do celular (P9.1): a pessoa escolhe o Secretário como assistente
 * digital do Android e passa a chamá-lo segurando o botão lateral. Quem escolhe
 * é ela, nas configurações do aparelho; aqui mostramos o estado e o caminho.
 */
export function AssistenteCelularPage() {
  const [, navigate] = useLocation();
  const profile = useProfile().data;
  const characterKey = profile?.characterKey ?? DEFAULT_CHARACTER;
  const name = profile?.secretaryName?.trim() || characterName(characterKey);
  const native = liveServiceAvailable();
  const [held, setHeld] = useState<boolean | null>(null);
  const [failed, setFailed] = useState(false);

  const refresh = useCallback(() => {
    void assistantHeld().then(setHeld);
  }, []);
  useEffect(() => {
    refresh();
    // Volta das configurações do aparelho: confere de novo.
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  const headline = !native
    ? 'No celular Android, eu atendo pelo botão lateral'
    : held === null
      ? 'Conferindo o seu aparelho'
      : held
        ? 'Eu sou o assistente do seu celular'
        : 'Me chame pelo botão lateral';

  return (
    <PageShell
      kicker="Assistente do celular"
      title={headline}
      lead={
        native
          ? `Com ${name} como assistente do celular, é só segurar o botão lateral para começar a conversa, de qualquer tela.`
          : 'No app para Android dá para me chamar segurando o botão lateral do celular.'
      }
      onBack={() => navigate('/ajustes')}
      backLabel="Voltar para Ajustes"
    >
      {native && held === null && <Skeleton variant="rows" label="Conferindo o seu aparelho" />}

      {native && held !== null && (
        <>
          <section className="pgs-section">
            <div className="pgs-row ct-row">
              <span className="pgs-ic">{held ? <GlyphCheckCircle /> : <GlyphPhone />}</span>
              <span className="pgs-rowt">
                <b>Assistente digital</b>
                <small>
                  {held
                    ? `Escolhido. Segure o botão lateral e ${name} atende.`
                    : 'Em "Aplicativo assistente digital", escolha o Secretário. Em alguns celulares ele fica em "Outros aplicativos".'}
                </small>
              </span>
              {held ? (
                <span className="ct-seal">Pronto</span>
              ) : (
                <button
                  type="button"
                  className="pgs-btn small"
                  onClick={() => void openAssistantSettings().then((ok) => setFailed(!ok))}
                >
                  Escolher
                </button>
              )}
            </div>
            {failed && (
              <p className="pgs-note" role="status">
                Não consegui abrir as configurações daqui. Procure "Assistente digital" nas
                configurações do celular.
              </p>
            )}
          </section>

          <section className="pgs-section">
            <h2 className="pgs-h2">Também funciona</h2>
            <div className="pgs-row ct-row">
              <span className="pgs-ic">
                <GlyphMic />
              </span>
              <span className="pgs-rowt">
                <b>Botão do fone</b>
                <small>
                  Segure o botão do fone de ouvido para me chamar. Durante a conversa, um toque
                  silencia.
                </small>
              </span>
            </div>
          </section>
        </>
      )}

      <p className="pgs-note">
        Em alguns celulares o botão lateral precisa ser configurado para abrir o assistente, em
        Recursos avançados ou Botão lateral.
      </p>
    </PageShell>
  );
}
